import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Col, Divider, Form, Row } from 'antd';
import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import { CheckoutOrderOverview } from 'app/components/CheckoutOrderOverview';
import { LoadingContainer } from 'app/components/LoadingContainer';
import ModalContainer from 'app/components/ModalContainer';
import StyledButton from 'app/components/StyledButton';
import { cancelOrderApi } from 'app/services/authApi';
import _ from 'lodash';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { notify } from 'reapop';
import styled from 'styled-components';
import { BP_TYPE, DEFAULT_LANGUAGE, TYPE_COOKIE } from 'utils/constants';
import { getCookie } from 'utils/cookies';
import { formatNumber, renderReplaceText, windowOpenLink } from 'utils/helper';
import { z } from 'zod';
import { useGlobalFunction } from '../App/hooks/useGlobalFunction';
import {
  selectInfoUser,
  selectIsLogged,
  selectTranslations,
} from '../App/slice/selectors';
import EditOrderPopup, {
  ADJUST_ORDER_STATE,
  EDIT_FLOW_TYPES,
  EDIT_FORM_VALUES,
  TICKET_TYPES,
} from './components/EditOrderPopup';
import { useCheckout } from './hooks/useCheckout';
import { GET_LIST_PAYMENT_METHOD } from './slice';
import { CheckoutWrapper } from './styled';
interface Props {}

const replacePaymentWithSpan = (
  text: string,
  replaceText: string,
  replaceTo: string,
  style: React.CSSProperties = {},
) => {
  const parts = text.split(replaceText);

  const newText = parts.map((part, index) => (
    <>
      {part}
      {index < parts.length - 1 && <span style={style}>{replaceTo}</span>}
    </>
  ));

  return <>{newText}</>;
};

export const ConfirmPolicyOrder = memo((props: Props) => {
  const [formInfoUser] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isAccept, setIsAccept] = useState(false);
  const [termTxt, setTermTxt] = useState('');
  const [isShowTimeout, setIsShowTimeout] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isLoadingCancel, setIsLoadingCancel] = useState<boolean>(false);

  const isLogged = useSelector(selectIsLogged);
  const translations = useSelector(selectTranslations);
  const {
    loading,
    INIT_FORM,
    totalAmount,
    totalPriceOriginal,
    totalPrice,
    setEnableConfirm,
    orderCheckout,
    totalDiscount,
    groupCart,
    vouchers,
    listCart,
    idOrder,
    payment,
    onSubmitForm,
    bankInfo,
    orderState,
    expiredHours,
    isValidPaymentTime,
    order,
    getOrderDetail,
  } = useCheckout();

  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const adjustedPriceOrFOC = !!order?.adjustedPriceOrFOC;

  const editOrderSchema = z.object({
    giftTickets: z.array(
      z
        .object({
          _productId: z.number(),
          amount: z.number().nullable(),
          unitPrice: z.number(),
          maxQuantityFOC: z.number().optional(),
          _original: z.object({
            amount: z.number(),
            unitPrice: z.number(),
          }),
          type: z.union([
            z.literal(TICKET_TYPES.FOC),
            z.literal(TICKET_TYPES.NORMAL_GIFT),
          ]),
        })
        .superRefine((data, ctx) => {
          if (
            typeof data.amount === 'number' &&
            data.type === TICKET_TYPES.FOC &&
            typeof data.maxQuantityFOC === 'number' &&
            data.amount > data.maxQuantityFOC
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_big,
              maximum: data.maxQuantityFOC,
              type: 'number',
              inclusive: true,
              path: ['amount'],
              message:
                translations?.['b2b.txt.ticket_limit_exceeded']?.replace(
                  '${max}',
                  data.maxQuantityFOC.toString(),
                ) ?? '',
            });
          }
        }),
    ),
    mainTickets: z.array(
      z
        .object({
          _productId: z.number(),
          amount: z.number(),
          unitPrice: z.number().nullable(),
          _original: z.object({
            amount: z.number(),
            unitPrice: z.number(),
          }),
          type: z.union([
            z.literal(TICKET_TYPES.MAIN),
            z.literal(TICKET_TYPES.ENTRANCE_TICKET),
          ]),
        })
        .superRefine((data, ctx) => {
          if (
            data._original.unitPrice !== 0 &&
            typeof data.unitPrice === 'number' &&
            data.unitPrice < 1
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_small,
              minimum: 1,
              type: 'number',
              inclusive: true,
              path: ['unitPrice'],
              message: translations?.['b2b.txt.invalidPrice'] ?? '',
            });
          }
        }),
    ),
    docs: z.any().optional(),
  });

  const editOrderForm = useForm<EDIT_FORM_VALUES>({
    defaultValues: {
      giftTickets: [],
      mainTickets: [],
    },
    resolver: zodResolver(editOrderSchema),
    mode: 'onChange',
  });

  const infoUser = useSelector(selectInfoUser);

  const isProxyBuyer =
    (infoUser?.businessPartner as any)?.bpType === BP_TYPE.PROXY_BUYER;

  const isProxyOrder = !!order?.businessPartnerAllocatedDebt?.id;

  const isEditableInProxyBuyingFlow = isProxyBuyer && isProxyOrder;

  const isHasFOC = listCart?.some(
    item => item?.higherLevelItem === TICKET_TYPES.FOC,
  );
  const isEditableInNormalBuyingFlow =
    !isProxyOrder &&
    isHasFOC &&
    !infoUser?.businessPartner?.notAllowToChangeFOC;

  const isEditable =
    isEditableInProxyBuyingFlow || isEditableInNormalBuyingFlow;

  const isEnableAdjustOrderInProxyBuyingFlow =
    isEditableInProxyBuyingFlow &&
    [ADJUST_ORDER_STATE.PAYMENT_PENDING].includes(orderState?.value) &&
    !adjustedPriceOrFOC;

  const isEnableAdjustOrderInNormalBuyingFlow =
    isEditableInNormalBuyingFlow &&
    [ADJUST_ORDER_STATE.PAYMENT_PENDING].includes(orderState?.value);

  const isEnableAdjustOrder =
    isEnableAdjustOrderInProxyBuyingFlow ||
    isEnableAdjustOrderInNormalBuyingFlow;

  const isHidePaymentDesc = [
    ADJUST_ORDER_STATE.PENDING_APPROVE,
    ADJUST_ORDER_STATE.INIT,
  ].includes(orderState?.value);

  const onViewPurcharseHistory = (orderState: string) => {
    switch (orderState) {
      case 'pendingApprove':
        return windowOpenLink({
          url: '/account?route=tab-5',
          target: '_self',
        });

      case 'init':
        return windowOpenLink({
          url: '/account?route=tab-4',
          target: '_self',
        });

      case 'cancelled':
        return windowOpenLink({
          url: '/account?route=tab-3',
          target: '_self',
        });

      case 'success':
        return windowOpenLink({
          url: '/account?route=tab-1',
          target: '_self',
        });

      case 'paymentPending':
      default:
        return windowOpenLink({
          url: '/account',
          target: '_self',
        });
    }
  };

  const handleRidrect = async () => {
    const data = await getOrderDetail();
    return onViewPurcharseHistory(data?.orderState?.value);
  };

  const onOpenEditModal = () => {
    setOpenEditModal(true);
  };

  const onCloseEditModal = () => {
    setOpenEditModal(false);
  };

  useEffect(() => {
    if (isShowTimeout) {
      onCloseDialogNoti();
      return;
    }
    if (order?.isFocAltered === 1 && !_.isEmpty(order?.alteredFocData)) {
      openFocConfirmDialog(order);
    }
  }, [order, isShowTimeout]);
  const openFocConfirmDialog = useCallback(
    currentOrder => {
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        maxWidth: 550,
        content: (
          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            {currentOrder?.alteredFocData?.map((el, index) => {
              return (
                <div key={index}>
                  ○&nbsp;
                  {renderReplaceText(
                    translations?.['b2b.txt.focTicketReplaced'],
                    {
                      foc1: {
                        value: el?.original_foc?.product_name,
                        style: { fontWeight: 'bold' },
                      },
                      selected_date: moment(
                        el?.alternative_foc?.date,
                        'YYYYMMDD',
                      ).format('DD/MM/YYYY'),
                      foc2: {
                        value: el?.alternative_foc?.product_name,
                        style: { fontWeight: 'bold' },
                      },
                    },
                  )}
                </div>
              );
            })}
            <NoteFoc>{translations?.['b2b.txt.confirmContinueOrder']}</NoteFoc>
          </div>
        ),
        title: translations?.['b2b.txt.confirmFocTicket'],
        closable: false,
        buttons: [
          {
            text: translations?.['b2b.txt.cancel-order'] ?? '',
            bg: '#ffff',
            border: 'solid 1px #808080',
            color: '#808080',
            onClick: () => {
              onCancelOrder(currentOrder);
            },
          },
          {
            text: translations?.['b2b.btn.accept'] ?? '',
            bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
            color: 'white',
            onClick: () => {
              onCloseDialogNoti();
            },
          },
        ],
      });
    },
    [translations, onCloseDialogNoti, onOpenDialogNoti, isLoadingCancel],
  );

  useEffect(() => {
    if (isLogged) formInfoUser.setFieldsValue({ ...INIT_FORM });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, INIT_FORM]);

  useEffect(() => {
    dispatch(
      GET_LIST_PAYMENT_METHOD({
        ticketType: sessionStorage.getItem('ticketType'),
      }),
    );
    // if (!cartLength) history.push('/');
    // else dispatch(GET_LIST_PAYMENT_METHOD());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTerm = async () => {
      const language =
        (await getCookie(TYPE_COOKIE.LANGUAGE)) || DEFAULT_LANGUAGE;
      fetch(`/policy-payment-${language}.txt`)
        .then(response => response.text())
        .then(data => {
          setTermTxt(data);
        });
    };
    // fetch('/term.html')
    //   .then(response => response.text())
    //   .then(data => {
    //     setTermHtml(data);
    //   });
    fetchTerm();
  }, []);

  useEffect(() => {
    if (payment?.code && payment?.code.toLowerCase() == 'banktransfer') {
      if (orderState?.value === 'success') {
        history.push({
          pathname: '/account',
          state: {
            orderState: 'success',
            payment: payment?.code,
          },
        });
      }
      if (orderState?.value === 'cancelled') {
        history.push({
          pathname: '/account',
          state: {
            orderState: 'cancelled',
            payment: payment?.code,
          },
        });
      }
    }
  }, [payment, orderState]);
  const onCancelOrder = async orderParam => {
    try {
      onCloseDialogNoti();
      setIsLoadingCancel(true);
      const formData = new FormData();
      formData.append('idHash', orderParam.idHash);
      const response = await cancelOrderApi(formData);
      if (response?.status === 200) {
        setIsLoadingCancel(false);
        history.push('/account?route=tab-3');
      }
    } catch (err: any) {
      setIsLoadingCancel(false);
      dispatch(
        notify(
          err?.message ?? translations?.['b2b.txt.cancelOrderFailed'] ?? '',
          'error',
          {
            title: 'Oops',
            dismissAfter: 5000,
          },
        ),
      );
    }
  };
  return (
    <CheckoutWrapper id="bookticketspay" className="main">
      <div className="bookticketspayContent bookticketsContent checkOut">
        <div className="container custom-container">
          <LoadingContainer isFullPage loading={loading || isLoadingCancel}>
            <div className="row">
              <div className="col-xl-8">
                {/* vnpay deposit */}
                {payment?.code &&
                  payment?.code.toLowerCase() !== 'banktransfer' && (
                    <>
                      <div className="checkOutInfo">
                        <HeaderContainer>
                          <Title>
                            {translations
                              ? translations[
                                  'b2c.txt.create-order-number'
                                ]?.replaceAll('${number}', `${idOrder ?? ''}`)
                              : ''}
                          </Title>
                        </HeaderContainer>
                        {!isHidePaymentDesc && (
                          <>
                            {expiredHours && isValidPaymentTime ? (
                              <Description>
                                {translations
                                  ? replacePaymentWithSpan(
                                      translations[
                                        'b2c.txt.pay-order-description'
                                      ],
                                      '${payment}',
                                      payment?.name ?? '',
                                      { fontWeight: 'bold' },
                                    )
                                  : ''}{' '}
                                {translations
                                  ? replacePaymentWithSpan(
                                      translations[
                                        'b2c.txt.pay-order-description-1'
                                      ],
                                      '${hour}',
                                      expiredHours + 'h',
                                      { color: '#E92127' },
                                    )
                                  : ''}
                              </Description>
                            ) : undefined}
                          </>
                        )}
                      </div>
                      <div className="checkOutInfo">
                        {isValidPaymentTime && (
                          <>
                            <HeaderContainer>
                              <Title>
                                {translations
                                  ? translations['b2c.txt.policyTitle']
                                  : ''}
                              </Title>
                            </HeaderContainer>
                            <Description>
                              {translations
                                ? translations['b2b.form-policy.description']
                                : ''}
                            </Description>
                            <StyledPolicy>
                              <Policy>
                                <SubHeaderContainer>
                                  <div style={{ textTransform: 'capitalize' }}>
                                    {translations
                                      ? translations['b2c.txt.paymentPolicy']
                                      : ''}
                                  </div>
                                </SubHeaderContainer>
                                <Description
                                  style={{
                                    textAlign: 'justify',
                                    marginRight: 8,
                                  }}
                                >
                                  {termTxt
                                    ?.replaceAll('\\n', '\n')
                                    .split('\n')
                                    .map((val, index) => (
                                      <div
                                        key={index}
                                        style={{ whiteSpace: 'pre-wrap' }}
                                      >
                                        {val}
                                        <br />
                                      </div>
                                    ))}
                                </Description>
                              </Policy>
                            </StyledPolicy>
                            <Checkbox
                              style={{ margin: '12px 0px' }}
                              onChange={e => {
                                setIsAccept(!isAccept);
                              }}
                            >
                              {translations
                                ? translations['b2c.txt.accept-policy']
                                : ''}
                            </Checkbox>
                            <StyledDivider
                              style={{
                                margin: '20px 0px 25px 0px',
                              }}
                              dashed
                            />
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <StyledButton
                                bgColor="white"
                                border="1px solid #808080"
                                textColor="#808080"
                                height={'44px'}
                                onClick={handleRidrect}
                                style={{
                                  marginRight: 16,
                                  width: 'max-content',
                                }}
                              >
                                {translations
                                  ? translations['b2b.txt.back-to-history']
                                  : ''}
                              </StyledButton>
                              <StyledButton
                                disabled={!isAccept}
                                border="none"
                                textColor="#ffffff"
                                onClick={() => {
                                  setEnableConfirm(false);
                                  setTimeout(() => onSubmitForm(), 100);
                                }}
                                height={'44px'}
                              >
                                <span style={{ marginRight: 8 }}>
                                  {translations
                                    ? translations['b2c.txt.confirm']
                                    : ''}
                                </span>
                                <i className="fa-solid fa-arrow-right"></i>
                              </StyledButton>
                            </div>
                          </>
                        )}
                        {!isValidPaymentTime && (
                          <div
                            className="--sub-title"
                            style={{ fontStyle: 'normal', textAlign: 'center' }}
                          >
                            {translations
                              ? `${translations[
                                  'b2b.txt.note-transfer-money-invalid'
                                ]?.replace('${payment}', payment.name)}`
                              : ''}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                {/* cash | banktransfer */}
                {payment?.code &&
                  payment?.code.toLowerCase() === 'banktransfer' && (
                    <>
                      <div className="checkOutInfo">
                        <HeaderContainer>
                          <Title>
                            {translations
                              ? translations[
                                  'b2c.txt.create-order-number'
                                ]?.replaceAll('${number}', `${idOrder ?? ''}`)
                              : ''}
                          </Title>
                        </HeaderContainer>
                        {!isHidePaymentDesc && (
                          <Description>
                            {expiredHours ? (
                              <>
                                {translations
                                  ? replacePaymentWithSpan(
                                      translations[
                                        'b2c.txt.pay-order-description'
                                      ],
                                      '${payment}',
                                      payment?.name ?? '',
                                      { fontWeight: 'bold' },
                                    )
                                  : ''}{' '}
                                {translations
                                  ? replacePaymentWithSpan(
                                      translations[
                                        'b2c.txt.pay-order-description-1'
                                      ],
                                      '${hour}',
                                      expiredHours + 'h',
                                      { color: '#E92127' },
                                    )
                                  : ''}
                              </>
                            ) : (
                              <>
                                {translations &&
                                  replacePaymentWithSpan(
                                    translations[
                                      'b2c.txt.pay-order-description'
                                    ],
                                    '${payment}',
                                    translations['b2b.txt.bank-transfer'],
                                    {
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                    },
                                  )}
                                {'. '}
                                {translations
                                  ? translations[
                                      'b2b.txt.pay-order-description-2'
                                    ]
                                  : ''}
                              </>
                            )}
                          </Description>
                        )}
                      </div>
                      <div className="checkOutInfo">
                        <HeaderContainer>
                          <Title>
                            {translations
                              ? translations['b2b.txt.paymentGuide']
                              : ''}
                          </Title>
                        </HeaderContainer>
                        {isValidPaymentTime && (
                          <>
                            <Description>
                              {translations
                                ? replacePaymentWithSpan(
                                    translations[
                                      'b2b.form-policy.bank_descriptions'
                                    ],
                                    '${totalPrice}',
                                    `${formatNumber(totalPrice)} đ` ?? '',
                                    { fontWeight: 'bold', color: '#E92127' },
                                  )
                                : ''}
                            </Description>
                            <Row nz-row className="d-flex txt-content">
                              <Col xs={8} md={4}>
                                {translations
                                  ? `${translations['b2b.txt.bank-account']}: `
                                  : ''}
                              </Col>
                              <Col xs={14} md={18}>
                                {bankInfo?.bankAccount}
                              </Col>
                            </Row>
                            <Row nz-row className="d-flex txt-content">
                              <Col xs={8} md={4}>
                                {translations
                                  ? `${translations['b2c.txt.bank']}: `
                                  : ''}
                              </Col>
                              <Col xs={14} md={18}>
                                {bankInfo?.bankName}
                              </Col>
                            </Row>
                            <Row nz-row className="d-flex txt-content">
                              <Col xs={8} md={4}>
                                {translations
                                  ? `${translations['b2c.txt.modal-ticket-content']}: `
                                  : ''}
                              </Col>
                              <Col xs={14} md={18}>
                                {idOrder}
                              </Col>
                            </Row>
                          </>
                        )}
                        <div
                          className="--sub-title"
                          style={
                            isValidPaymentTime
                              ? { margin: '17px 0px' }
                              : { fontStyle: 'normal', textAlign: 'center' }
                          }
                        >
                          {isValidPaymentTime
                            ? translations
                              ? `${translations['b2b.txt.note-transfer-money']}: `
                              : ''
                            : translations
                            ? `${translations[
                                'b2b.txt.note-transfer-money-invalid'
                              ]?.replace('${payment}', payment?.name ?? '')}`
                            : ''}
                        </div>
                        {isValidPaymentTime && (
                          <>
                            <div className="d-flex txt-content">
                              {translations
                                ? translations['b2b.txt.QR-code']
                                : ''}
                            </div>
                            <ImgWrapper>
                              <div className="d-flex justify-content-center">
                                <StyledImg
                                  src={bankInfo?.qrCode?.original}
                                  alt=""
                                />
                              </div>
                            </ImgWrapper>
                          </>
                        )}

                        <StyledDivider
                          style={{
                            margin: '20px 0px 25px 0px',
                          }}
                          dashed
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <StyledButton
                            bgColor="white"
                            border="1px solid #808080"
                            textColor="#808080"
                            height={'44px'}
                            onClick={() => handleRidrect()}
                            style={{
                              marginRight: 16,
                              width: 'auto',
                              minWidth: 160,
                            }}
                          >
                            {translations
                              ? translations['b2b.txt.back-to-history']
                              : ''}
                          </StyledButton>
                        </div>
                      </div>
                    </>
                  )}
              </div>
              <div className="col-xl-4">
                <CheckoutOrderOverview
                  vouchers={vouchers}
                  headerText={`${translations?.['b2b.txt.order'] ?? ''}: ${
                    idOrder ?? ''
                  }`}
                  isDisableCountDown={
                    !Boolean(orderCheckout?.current?.expiredAt)
                  }
                  listCart={listCart}
                  orderCheckoutRef={orderCheckout}
                  isHideButton={true}
                  listGroupCart={groupCart}
                  totalAmount={totalAmount}
                  totalDiscount={totalDiscount}
                  totalPrice={totalPrice}
                  totalPriceOriginal={totalPriceOriginal}
                  onTimeout={() => {
                    setIsShowTimeout(true);
                    setEnableConfirm(false);
                  }}
                  onCheckout={() => {}}
                  extraTitle={
                    isEditable ? (
                      <button
                        className="mb-3"
                        style={{
                          color: isEnableAdjustOrder ? '#e92127' : '#9d9d9d',
                          fontWeight: 500,
                          userSelect: 'none',
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          padding: 0,
                        }}
                        onClick={onOpenEditModal}
                        disabled={!isEnableAdjustOrder}
                      >
                        {translations?.['b2b.btn.edit'] ?? ''}
                      </button>
                    ) : null
                  }
                />
              </div>
            </div>
          </LoadingContainer>
        </div>
      </div>
      {/* <ExitConfirm
        message={translations ? translations['b2c.txt.directOut'] : ''}
        enable={enableConfirm}
      /> */}
      <TimeoutModal
        open={isShowTimeout}
        onClose={() => {
          onCloseDialogNoti();
          history.push(
            '/payment-callback?vnp_TxnRef=' + orderCheckout.current?.idHash,
          );
        }}
      />
      <FormProvider {...editOrderForm}>
        <EditOrderPopup
          open={openEditModal}
          onClose={onCloseEditModal}
          groupTicketData={groupCart}
          idHash={order?.idHash ?? ''}
          getOrderDetail={getOrderDetail}
          buyingFlow={
            isEditableInProxyBuyingFlow
              ? EDIT_FLOW_TYPES.PROXY_BUYING
              : EDIT_FLOW_TYPES.NORMAL_BUYING
          }
        />
      </FormProvider>
    </CheckoutWrapper>
  );
});

export const TimeoutModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose?: () => void;
}) => {
  const translations = useSelector(selectTranslations);
  return (
    <ModalContainer
      isShow={open}
      maskClosable={false}
      onCloseDialog={onClose}
      CloseIcon={<CloseIcon width={12} height={12} />}
      styleSlots={{
        iconWrapperStyle: {
          margin: '-8px 0px 0px 28px',
          width: 12,
          height: 12,
        },
        modalContentStyle: {
          height: 'auto',
          width: 'auto',
          padding: '38px 31px',
          borderRadius: '16px',
        },
        modalStyle: {
          width: 'auto',
          maxWidth: '280px',
        },
        bodyStyle: {
          width: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        },
      }}
    >
      <div>{translations ? translations['b2b.txt.timeoutPayment'] : ''}</div>
      <div>{translations ? translations['b2b.txt.retry'] : ''}</div>
    </ModalContainer>
  );
};

const Policy = styled.div`
  background: #ffffff;
  overflow: auto;
  height: 600px;
  ::-webkit-scrollbar {
    width: 6px; /* Width of the scrollbar */
  }

  /* Define the track (the area behind the thumb) */
  ::-webkit-scrollbar-track {
    background: transparent; /* Color of the track */
  }

  /* Define the scrollbar thumb */
  ::-webkit-scrollbar-thumb {
    background: #ececec; /* Color of the thumb */
    border-radius: 20px;
  }

  /* Define the scrollbar thumb on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #d6d6d6; /* Color of the thumb on hover */
  }
`;

const StyledPolicy = styled.div`
  border-radius: 20px;
  background: #ffffff;
  /* height: 301px; */
  padding: 24px 20px;
  border: 1px solid #d9d9d9;
  color: #000000;
  margin-top: 12px;
`;

const Title = styled.h4`
  margin: 0;
  font-weight: 600;
  color: #000000;
  font-size: 24px;
  text-transform: capitalize;
`;

const Description = styled.p`
  font-weight: 400;
  color: #000000;
  font-size: 14px;
  text-align: left;
  margin-top: 14px;
  line-height: 1.7rem;
`;

const HeaderContainer = styled.div`
  margin: 10px 0px;
  display: flex;
  justify-content: center;
  color: #000000;
`;
const SubHeaderContainer = styled.div`
  margin: 10px 0px;
  display: flex;
  color: #000000;
  font-size: 16px;
  font-weight: 600;
`;

const StyledDivider = styled(Divider)`
  margin: 14px 0px 14px 0px;
  width: 100%;
  min-width: 100%;
`;
const ImgWrapper = styled.div`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
`;
const StyledImg = styled.img`
  width: 250px;
  max-width: 100%;
  height: 'auto';
  display: 'flex';
  justify-content: 'center';
  object-fit: cover;
  &[src=''] {
    opacity: 0;
  }
`;
const NoteFoc = styled.div`
  margin-top: 12px;
  text-align: center;
`;
